import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import DatePicker from "@/components/DatePicker";
import { formatMoney, formatRate, monthLabel } from "@/lib/format";
import { AlertTriangle, AlertCircle, ChevronDown, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

function monthsBetween(startISO, endISO) {
  if (!startISO || !endISO) return [];
  const s = new Date(startISO + "T00:00:00");
  const e = new Date(endISO + "T00:00:00");
  if (e < s) return [];
  const keys = [];
  let y = s.getFullYear(), m = s.getMonth();
  while (y < e.getFullYear() || (y === e.getFullYear() && m <= e.getMonth())) {
    keys.push(`${y}-${String(m + 1).padStart(2, "0")}`);
    m++; if (m > 11) { m = 0; y++; }
  }
  return keys;
}

export default function CreateDividend() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [company, setCompany] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [shareholders, setShareholders] = useState([]);

  const [periodKey, setPeriodKey] = useState("");
  const [periodType, setPeriodType] = useState("quarterly");
  const [form, setForm] = useState({
    description: "Quarterly Dividend", period_start: null, period_end: null,
    record_date: null, payment_date: null, voucher_issue_date: null,
    share_class: "", signatory_name: "", signatory_position: "Director", notes: "",
  });
  const [months, setMonths] = useState([]);
  const [rates, setRates] = useState({});
  const [sameRate, setSameRate] = useState("");
  const [selectedShIds, setSelectedShIds] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/companies").then((res) => {
      setCompanies(res.data);
      if (res.data.length) setCompanyId(res.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}`).then((r) => {
      setCompany(r.data);
      set("share_class", r.data.share_classes?.[0] || "Ordinary");
      set("signatory_name", r.data.default_signatory_name || "");
      set("signatory_position", r.data.signatory_title || "Director");
      setPeriodType(r.data.default_dividend_frequency === "monthly" ? "monthly" : "quarterly");
    });
    api.get(`/companies/${companyId}/shareholders?include_archived=false`).then((r) => setShareholders(r.data));
    setSelectedShIds([]); setOverrides({}); setPreview(null); setPeriodKey(""); setMonths([]);
  }, [companyId]);

  // fetch available periods for the chosen frequency
  useEffect(() => {
    if (!companyId || periodType === "custom") { setPeriods([]); return; }
    api.get(`/companies/${companyId}/periods`, { params: { frequency: periodType } }).then((r) => setPeriods(r.data));
  }, [companyId, periodType]);

  const changePeriodType = (t) => {
    setPeriodType(t);
    setPeriodKey(""); setMonths([]); setRates({});
    setForm((f) => {
      const known = ["Quarterly Dividend", "Monthly Dividend", "Dividend"];
      const desc = known.includes(f.description)
        ? (t === "monthly" ? "Monthly Dividend" : t === "quarterly" ? "Quarterly Dividend" : "Dividend")
        : f.description;
      return { ...f, description: desc, period_start: null, period_end: null };
    });
  };

  const isQuarterly = periodType === "custom" ? months.length === 3 : periodType === "quarterly";

  const applyPeriod = (key) => {
    setPeriodKey(key);
    const p = periods.find((x) => x.start === key);
    if (p) {
      set("period_start", p.start);
      set("period_end", p.end);
      setMonths(p.months);
      const nr = {}; p.months.forEach((m) => { nr[m] = rates[m] || ""; });
      setRates(nr);
    }
  };

  // custom period months follow start/end
  useEffect(() => {
    if (periodType === "custom") {
      const mk = monthsBetween(form.period_start, form.period_end);
      setMonths(mk);
      setRates((prev) => { const nr = {}; mk.forEach((m) => { nr[m] = prev[m] || ""; }); return nr; });
    }
  }, [periodType, form.period_start, form.period_end]);

  const eligibleShareholders = useMemo(
    () => shareholders.filter((s) => (s.shareholdings || []).some((h) => h.share_class === form.share_class)),
    [shareholders, form.share_class]
  );

  const toggleSh = (id) => setSelectedShIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const selectAll = () => setSelectedShIds(eligibleShareholders.map((s) => s.id));
  const clearAll = () => setSelectedShIds([]);

  const applySameRate = () => {
    const nr = {}; months.forEach((m) => { nr[m] = sameRate; }); setRates(nr);
  };

  const buildPayload = useCallback(() => ({
    company_id: companyId,
    months,
    share_class: form.share_class,
    monthly_rates: rates,
    share_overrides: overrides,
    shareholder_ids: selectedShIds,
    period_start: form.period_start,
    period_end: form.period_end,
    record_date: form.record_date,
    payment_date: form.payment_date,
    voucher_issue_date: form.voucher_issue_date,
    signatory_name: form.signatory_name,
    is_quarterly: isQuarterly,
  }), [companyId, months, form, rates, overrides, selectedShIds, isQuarterly]);

  // debounced preview
  useEffect(() => {
    if (!companyId || months.length === 0) { setPreview(null); return; }
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await api.post("/dividends/preview", buildPayload());
        setPreview(res.data);
      } catch (e) {} finally { setPreviewing(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [buildPayload, companyId, months.length]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...buildPayload(), description: form.description, signatory_position: form.signatory_position, notes: form.notes,
        period_label: periodType === "custom"
          ? (months.length === 1 ? monthLabel(months[0]) : `${monthLabel(months[0])} to ${monthLabel(months[months.length - 1])}`)
          : (periods.find((p) => p.start === periodKey)?.label || "") };
      const res = await api.post("/dividends", body);
      toast.success("Draft dividend saved");
      navigate(`/dividends/${res.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const errors = (preview?.warnings || []).filter((w) => w.level === "error");
  const warns = (preview?.warnings || []).filter((w) => w.level === "warning");

  return (
    <div data-testid="create-dividend-page">
      <PageHeader title="Create Dividend" description="Enter a dividend declaration with variable monthly rates." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: form */}
        <div className="space-y-6 lg:col-span-3">
          <Card className="border-cass-border p-6 shadow-sm">
            <h3 className="mb-4 font-head text-lg font-semibold text-cass-navy">Company & period</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Company</Label>
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger data-testid="cd-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent className="bg-white">{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Share class</Label>
                <Select value={form.share_class} onValueChange={(v) => set("share_class", v)}>
                  <SelectTrigger data-testid="cd-share-class"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{(company?.share_classes || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Period type</Label>
                <Select value={periodType} onValueChange={changePeriodType}>
                  <SelectTrigger data-testid="cd-period-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="custom">Custom period</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {periodType !== "custom" ? (
                <div className="space-y-1.5">
                  <Label>Dividend period</Label>
                  <Select value={periodKey} onValueChange={applyPeriod}>
                    <SelectTrigger data-testid="cd-period"><SelectValue placeholder={periodType === "monthly" ? "Select month" : "Select quarter"} /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">{periods.map((p) => <SelectItem key={p.start} value={p.start}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5"><Label>Period start</Label><DatePicker value={form.period_start} onChange={(v) => set("period_start", v)} testId="cd-custom-start" /></div>
                  <div className="space-y-1.5"><Label>Period end</Label><DatePicker value={form.period_end} onChange={(v) => set("period_end", v)} testId="cd-custom-end" /></div>
                </>
              )}
            </div>
          </Card>

          <Card className="border-cass-border p-6 shadow-sm">
            <h3 className="mb-4 font-head text-lg font-semibold text-cass-navy">Dividend details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5"><Label>Description</Label><Input data-testid="cd-description" value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Record date</Label><DatePicker value={form.record_date} onChange={(v) => set("record_date", v)} testId="cd-record-date" /></div>
              <div className="space-y-1.5"><Label>Payment date</Label><DatePicker value={form.payment_date} onChange={(v) => set("payment_date", v)} testId="cd-payment-date" /></div>
              <div className="space-y-1.5"><Label>Voucher issue date</Label><DatePicker value={form.voucher_issue_date} onChange={(v) => set("voucher_issue_date", v)} testId="cd-issue-date" /></div>
              <div className="space-y-1.5"><Label>Signatory name</Label><Input data-testid="cd-signatory" value={form.signatory_name} onChange={(e) => set("signatory_name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Signatory position</Label><Input data-testid="cd-signatory-position" value={form.signatory_position} onChange={(e) => set("signatory_position", e.target.value)} /></div>
              <div className="col-span-2 space-y-1.5"><Label>Internal notes (optional)</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
            </div>
          </Card>

          {months.length > 0 && (
            <Card className="border-cass-border p-6 shadow-sm">
              <h3 className="mb-4 font-head text-lg font-semibold text-cass-navy">Monthly dividend rates</h3>
              <div className="mb-4 flex items-end gap-2">
                <div className="space-y-1.5"><Label className="text-xs">Apply same rate to every month</Label>
                  <Input type="number" step="0.0001" placeholder="0.0000" value={sameRate} data-testid="cd-same-rate" onChange={(e) => setSameRate(e.target.value)} className="w-40" /></div>
                <Button type="button" variant="outline" onClick={applySameRate} data-testid="cd-apply-same">Apply</Button>
              </div>
              <div className="space-y-2">
                {months.map((m) => (
                  <div key={m} className="flex items-center gap-4">
                    <span className="w-40 text-sm font-medium text-cass-navy">{monthLabel(m)}</span>
                    <div className="relative w-48">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-cass-navy/50">£</span>
                      <Input type="number" step="0.0001" min="0" className="pl-7 tabular" value={rates[m] ?? ""} data-testid={`cd-rate-${m}`}
                        onChange={(e) => setRates((r) => ({ ...r, [m]: e.target.value }))} />
                    </div>
                    <span className="text-xs text-cass-navy/40">per share</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="border-cass-border p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-head text-lg font-semibold text-cass-navy">Shareholders</h3>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={selectAll} data-testid="cd-select-all">Select all eligible</Button>
                <Button type="button" variant="outline" size="sm" onClick={clearAll} data-testid="cd-clear-all">Clear</Button>
              </div>
            </div>
            {eligibleShareholders.length === 0 ? (
              <p className="text-sm text-cass-navy/50">No active shareholders hold {form.share_class} shares.</p>
            ) : (
              <div className="space-y-2">
                {eligibleShareholders.map((s) => {
                  const holding = (s.shareholdings || []).find((h) => h.share_class === form.share_class);
                  return (
                    <label key={s.id} data-testid={`cd-sh-${s.id}`}
                      className="flex cursor-pointer items-center gap-3 rounded-md border border-cass-border p-3 transition-colors hover:bg-cass-surface">
                      <Checkbox checked={selectedShIds.includes(s.id)} onCheckedChange={() => toggleSh(s.id)} data-testid={`cd-sh-check-${s.id}`} />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-cass-navy">{s.is_company && s.company_name ? s.company_name : s.full_name}</div>
                        <div className="text-xs text-cass-navy/50 tabular">{holding?.number_of_shares} {form.share_class} shares</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Right: preview */}
        <div className="lg:col-span-2">
          <div className="sticky top-8 space-y-4">
            <Card className="border-cass-border p-6 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-head text-lg font-semibold text-cass-navy">Calculation preview</h3>
                {previewing && <Loader2 className="h-4 w-4 animate-spin text-cass-blue" />}
              </div>

              {errors.map((w, i) => (
                <Alert key={`e${i}`} className="mb-2 border-red-200 bg-red-50" data-testid="preview-error">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">{w.message}</AlertDescription>
                </Alert>
              ))}
              {warns.map((w, i) => (
                <Alert key={`w${i}`} className="mb-2 border-amber-200 bg-amber-50" data-testid="preview-warning">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">{w.message}</AlertDescription>
                </Alert>
              ))}

              {!preview || preview.lines.length === 0 ? (
                <p className="text-sm text-cass-navy/50">Select shareholders and enter rates to preview.</p>
              ) : (
                <div className="space-y-2">
                  {preview.lines.map((line) => (
                    <Collapsible key={line.shareholder_id} className="rounded-md border border-cass-border">
                      <CollapsibleTrigger className="flex w-full items-center justify-between p-3" data-testid={`preview-line-${line.shareholder_id}`}>
                        <span className="text-sm font-medium text-cass-navy">{line.recipient_name}</span>
                        <span className="flex items-center gap-2">
                          <span className="tabular text-sm font-semibold text-cass-navy">{formatMoney(line.total)}</span>
                          <ChevronDown className="h-4 w-4 text-cass-navy/40" />
                        </span>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="border-t border-cass-border p-3">
                        <table className="w-full text-xs">
                          <thead><tr className="text-cass-navy/50">
                            <th className="text-left font-medium">Month</th><th className="text-right font-medium">Eligible shares</th>
                            <th className="text-right font-medium">Rate</th><th className="text-right font-medium">Payable</th>
                          </tr></thead>
                          <tbody>
                            {line.monthly.map((mm) => (
                              <tr key={mm.month_key}>
                                <td className="py-1 text-cass-navy/70">{mm.month_label}</td>
                                <td className="py-1 text-right">
                                  <Input type="number" className="ml-auto h-7 w-24 tabular text-right text-xs" data-testid={`override-${line.shareholder_id}-${mm.month_key}`}
                                    value={overrides[line.shareholder_id]?.[mm.month_key] ?? mm.eligible_shares}
                                    onChange={(e) => setOverrides((o) => ({ ...o, [line.shareholder_id]: { ...(o[line.shareholder_id] || {}), [mm.month_key]: e.target.value } }))} />
                                </td>
                                <td className="py-1 text-right tabular text-cass-navy/70">{formatRate(mm.rate)}</td>
                                <td className="py-1 text-right tabular font-medium text-cass-navy">{formatMoney(mm.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                  <div className="flex items-center justify-between border-t border-cass-border pt-3">
                    <span className="text-sm font-semibold text-cass-navy">Total across all shareholders</span>
                    <span data-testid="preview-grand-total" className="tabular text-lg font-bold text-cass-navy">{formatMoney(preview.grand_total)}</span>
                  </div>
                </div>
              )}
            </Card>

            <Button onClick={save} disabled={saving || !companyId} data-testid="cd-save-draft"
              className="h-11 w-full bg-cass-blue text-white hover:bg-cass-bluehover">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save as Draft
            </Button>
            <p className="text-center text-xs text-cass-navy/40">Finalisation and PDF generation happen on the record page.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
